import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('mouseover', this.handleMaterialMouseOver.bind(this));
    this.addEventListener('mouseout', this.handleMaterialMouseOut.bind(this));
    this.addEventListener('focusin', this.handleMaterialFocusIn.bind(this));
    this.addEventListener('focusout', this.handleMaterialFocusOut.bind(this));
    this.addEventListener('click', this.handleMaterialClick.bind(this), true);
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.updateSelectedOptionLabel(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
 * Updates selected option value text inside fieldset legend.
 * @param {Element} target
 */
  updateSelectedOptionLabel(target) {
    const fieldset = target.closest('fieldset');

    if (!fieldset) return;

    const selectedValueElement = fieldset.querySelector('[data-selected-option-value]');

    if (!selectedValueElement) return;

    if (target instanceof HTMLInputElement) {
      selectedValueElement.textContent = target.value;
    }

    if (target instanceof HTMLSelectElement) {
      selectedValueElement.textContent = target.value;
    }
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  getMaterialTrigger(target) {
    if (!(target instanceof Element)) return null;

    return target.closest('[data-material-option-trigger]');
  }

  /**
   * @returns {boolean}
   */
  isMobileMaterialMode() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  /**
   * @param {MouseEvent} event
   */
  handleMaterialMouseOver(event) {
    if (this.isMobileMaterialMode()) return;

    const trigger = this.getMaterialTrigger(event.target);

    if (!trigger || !this.contains(trigger)) return;

    this.showMaterialPopover(trigger);
  }

  /**
   * @param {MouseEvent} event
   */
  handleMaterialMouseOut(event) {
    if (this.isMobileMaterialMode()) return;

    const trigger = this.getMaterialTrigger(event.target);

    if (!trigger || !this.contains(trigger)) return;

    const relatedTarget = event.relatedTarget;

    if (relatedTarget instanceof Node && trigger.contains(relatedTarget)) return;

    this.hideMaterialPopover();
  }

  /**
   * @param {FocusEvent} event
   */
  handleMaterialFocusIn(event) {
    if (this.isMobileMaterialMode()) return;

    const trigger = this.getMaterialTrigger(event.target);

    if (!trigger || !this.contains(trigger)) return;

    this.showMaterialPopover(trigger);
  }

  /**
   * @param {FocusEvent} event
   */
  handleMaterialFocusOut(event) {
    if (this.isMobileMaterialMode()) return;

    const trigger = this.getMaterialTrigger(event.target);

    if (!trigger || !this.contains(trigger)) return;

    this.hideMaterialPopover();
  }

  /**
   * Opens material selection modal/drawer only below desktop.
   *
   * 0–749px: bottom sheet.
   * 750–989px: right drawer.
   * 990px+: native variant selection by radio input.
   *
   * @param {MouseEvent} event
   */
  handleMaterialClick(event) {
    const trigger = this.getMaterialTrigger(event.target);

    if (!trigger || !this.contains(trigger)) return;

    if (!this.isMobileMaterialMode()) {
      this.hideMaterialPopover();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.hideMaterialPopover();
    this.openMaterialModal(trigger);
  }

  /**
   * @param {HTMLElement} trigger
   */
  showMaterialPopover(trigger) {
    const template = trigger.querySelector('[data-material-template]');

    if (!(template instanceof HTMLTemplateElement)) return;

    const popover = this.getMaterialPopover();

    popover.innerHTML = template.innerHTML;

    const note = this.dataset.materialNote || '';

    if (note) {
      const noteElement = document.createElement('div');
      noteElement.className = 'material-option-template__note';
      noteElement.textContent = note;
      popover.querySelector('.material-option-template')?.appendChild(noteElement);
    }

    popover.classList.add('is-active');

    const triggerRect = trigger.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const spacing = 16;

    const triggerCenterY = triggerRect.top + triggerRect.height / 2;

    let left = triggerRect.left - popoverRect.width - spacing;
    let top = triggerCenterY - popoverRect.height / 2;

    if (left < spacing) {
      left = triggerRect.right + spacing;
    }

    if (left + popoverRect.width > window.innerWidth - spacing) {
      left = window.innerWidth - popoverRect.width - spacing;
    }

    if (top < spacing) {
      top = spacing;
    }

    if (top + popoverRect.height > window.innerHeight - spacing) {
      top = window.innerHeight - popoverRect.height - spacing;
    }

    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  }

  /**
   * Hides material popover.
   */
  hideMaterialPopover() {
    const popover = document.querySelector('.material-option-popover');

    if (!popover) return;

    popover.classList.remove('is-active');
  }

  /**
   * @returns {HTMLElement}
   */
  getMaterialPopover() {
    let popover = document.querySelector('.material-option-popover');

    if (!(popover instanceof HTMLElement)) {
      popover = document.createElement('div');
      popover.className = 'material-option-popover';
      document.body.appendChild(popover);
    }

    return popover;
  }

  /**
   * @param {HTMLElement} trigger
   */
  openMaterialModal(trigger) {
    const fieldset = trigger.closest('fieldset');

    if (!(fieldset instanceof HTMLFieldSetElement)) return;

    const triggers = Array.from(fieldset.querySelectorAll('[data-material-option-trigger]')).filter((item) => {
      return item instanceof HTMLElement && item.querySelector('[data-material-template]');
    });

    if (!triggers.length) return;

    const optionName = trigger.dataset.materialOptionName || '';
    const modal = this.getMaterialModal();
    const note = this.dataset.materialNote || '';
    const closeIcon = this.dataset.closeIcon || '';

    modal.innerHTML = `
    <div class="material-option-modal__overlay" data-material-modal-close></div>

    <div class="material-option-modal__dialog" role="dialog" aria-modal="true" aria-label="${this.escapeHtml(optionName)}" data-lenis-prevent>
      <header class="material-option-modal__header">
        <button type="button" class="material-option-modal__close" data-material-modal-close aria-label="Close">
          ${closeIcon
        ? `<img src="${this.escapeHtml(closeIcon)}" alt="" width="20" height="20" loading="lazy">`
        : ''
      }
        </button>

        <h3 class="material-option-modal__heading">
          ${this.escapeHtml(optionName)}
        </h3>
      </header>

      <div class="material-option-modal__body" data-lenis-prevent>
        <div class="material-option-modal__thumbs" data-material-modal-thumbs></div>
        <div class="material-option-modal__main" data-material-modal-main></div>

        ${note
        ? `<div class="material-option-modal__note">${this.escapeHtml(note)}</div>`
        : ''
      }
      </div>

      <footer class="material-option-modal__footer">
        <button type="button" class="material-option-modal__select" data-material-modal-select>
          Select material
        </button>
      </footer>
    </div>
  `;

    modal.classList.add('is-active');
    modal.dataset.activeOptionValue = trigger.dataset.materialOptionValue || '';
    document.documentElement.classList.add('material-option-modal-open');

    requestAnimationFrame(() => {
      modal.classList.add('is-open');
    });

    const thumbs = modal.querySelector('[data-material-modal-thumbs]');

    triggers.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const image = item.querySelector('.variant-option__material-image');
      const value = item.dataset.materialOptionValue || '';

      if (!(image instanceof HTMLImageElement)) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'material-option-modal__thumb';
      button.dataset.materialOptionValue = value;
      button.innerHTML = `<img src="${image.currentSrc || image.src}" alt="${this.escapeHtml(image.alt || value)}">`;

      if (item === trigger) {
        button.classList.add('is-active');
      }

      button.addEventListener('click', () => {
        modal.dataset.activeOptionValue = value;

        modal.querySelectorAll('.material-option-modal__thumb').forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb === button);
        });

        this.updateMaterialModalMain(modal, item);
      });

      thumbs?.appendChild(button);
    });

    this.updateMaterialModalMain(modal, trigger);

    modal.querySelectorAll('[data-material-modal-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeMaterialModal(), { once: true });
    });

    modal.querySelector('[data-material-modal-select]')?.addEventListener(
      'click',
      () => {
        const activeValue = modal.dataset.activeOptionValue;
        const activeTrigger = triggers.find((item) => item.dataset.materialOptionValue === activeValue);
        const input = activeTrigger?.querySelector('input');

        if (input instanceof HTMLInputElement) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.closeMaterialModal();
      },
      { once: true }
    );

    const closeButton = modal.querySelector('.material-option-modal__close');

    if (closeButton instanceof HTMLElement) {
      closeButton.focus();
    }

    this.bindMaterialModalScroll(modal);
  }

  /**
 * @param {HTMLElement} modal
 * @param {HTMLElement} trigger
 */
  updateMaterialModalMain(modal, trigger) {
    const template = trigger.querySelector('[data-material-template]');
    const main = modal.querySelector('[data-material-modal-main]');

    if (!(template instanceof HTMLTemplateElement) || !(main instanceof HTMLElement)) return;

    main.innerHTML = template.innerHTML;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  escapeHtml(value) {
    return value.replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };

      return entities[character] || character;
    });
  }  /**
   * Keeps wheel/touch scroll inside the material modal body.
   * This is needed because Lenis is enabled globally on product pages.
   *
   * @param {HTMLElement} modal
   */
  bindMaterialModalScroll(modal) {
    const scrollArea = modal.querySelector('.material-option-modal__body');

    if (!(scrollArea instanceof HTMLElement)) return;

    const stopScrollPropagation = (event) => {
      event.stopPropagation();
    };

    scrollArea.addEventListener('wheel', stopScrollPropagation, {
      passive: true,
    });

    scrollArea.addEventListener('touchmove', stopScrollPropagation, {
      passive: true,
    });

    const thumbs = modal.querySelector('.material-option-modal__thumbs');

    if (thumbs instanceof HTMLElement) {
      thumbs.addEventListener('wheel', stopScrollPropagation, {
        passive: true,
      });

      thumbs.addEventListener('touchmove', stopScrollPropagation, {
        passive: true,
      });
    }
  }



  /**
   * Closes material modal.
   */
  closeMaterialModal() {
    const modal = document.querySelector('.material-option-modal');

    if (!(modal instanceof HTMLElement)) return;

    modal.classList.remove('is-open');
    document.documentElement.classList.remove('material-option-modal-open');

    window.setTimeout(() => {
      modal.classList.remove('is-active');
      modal.innerHTML = '';
    }, 360);
  }

  /**
   * @returns {HTMLElement}
   */
  getMaterialModal() {
    let modal = document.querySelector('.material-option-modal');

    if (!(modal instanceof HTMLElement)) {
      modal = document.createElement('div');
      modal.className = 'material-option-modal';
      document.body.appendChild(modal);
    }

    return modal;
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
